/**
 * `UserAdmin` — pagina admin per la gestione di utenti, ruoli e reparto.
 *
 * Design ref: "Componenti chiave" → pagine admin; rotta `/admin/users`
 * (admin: gestione utenti/ruoli/assegnazioni, Req. 3.1–3.5, 4.2, 4.3).
 *
 * Operazioni sull'API (tutte riservate ad `admin`):
 *  - `GET   /api/users`                 → elenco utenti (Role + Department);
 *  - `PATCH /api/users/:id/role`        → modifica del Role (Req. 3.1);
 *  - `PATCH /api/users/:id/department`  → assegnazione del singolo Department,
 *    con `null` per disassociare (Req. 3.2, 3.3, 4.2, 4.3);
 *  - `PUT   /api/users/:id/ai-enabled`  → attiva/disattiva la valutazione AI
 *    (`aiEnabled`) dello User (Req. 7.1);
 *  - `PUT   /api/users/:id/levels-enabled` → attiva/disattiva l'accesso alla
 *    sezione "Livelli" (`levelsEnabled`) dello User.
 *
 * L'elenco dei reparti (`GET /api/departments`) fornisce le opzioni di
 * assegnazione. L'autorizzazione autorevole è dell'API (403 per i non‑admin,
 * Req. 3.4); le guardie lato client (`RoleRoute`) sono difensive.
 *
 * La tabella è costruita sul componente generico `DataTable` (design system):
 * elenco ordinabile (nome, email, ruolo, reparto) e paginato. Le mutazioni di
 * ruolo/reparto sono inline e invalidano la query dell'elenco.
 *
 * _Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 4.2, 4.3, 7.1._
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DepartmentDto,
  ImportUserRow,
  ImportUsersRequest,
  ImportUsersResponse,
  Role,
  UpdateUserRoleRequest,
  UserDto,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { parseUsersCsv, CsvParseError } from '../utils/parseUsersCsv';
import {
  Alert,
  Button,
  DataTable,
  Field,
  Loading,
  Modal,
  PageHeader,
  TextInput,
  Toggle,
  type Column,
  type SortDirection,
} from '../components/ui';

const usersKey = ['users'] as const;
const departmentsKey = ['departments'] as const;

/** Ordine e valori dei ruoli assegnabili (allineati a `Role`). */
const ROLES: Role[] = ['admin', 'manager', 'reviewer', 'employer'];

/** Etichette leggibili dei ruoli applicativi. */
const ROLE_LABEL: Record<Role, string> = {
  admin: 'Amministratore',
  manager: 'Manager',
  // Il valore del ruolo resta `reviewer` a DB/API; nella UI è mostrato come
  // "Team Leader": valuta i membri del proprio team.
  reviewer: 'Team Leader',
  employer: 'Dipendente',
};

/** Confronto di stringhe già orientato per la direzione. */
function makeStringComparator(accessor: (u: UserDto) => string) {
  return (a: UserDto, b: UserDto, direction: SortDirection): number => {
    const sign = direction === 'desc' ? -1 : 1;
    const va = accessor(a);
    const vb = accessor(b);
    if (va < vb) return -sign;
    if (va > vb) return sign;
    return 0;
  };
}

export default function UserAdmin() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const usersQuery = useQuery({
    queryKey: usersKey,
    queryFn: () => api.get<UserDto[]>('/api/users'),
  });
  const departmentsQuery = useQuery({
    queryKey: departmentsKey,
    queryFn: () => api.get<DepartmentDto[]>('/api/departments'),
  });

  const invalidateUsers = () =>
    void queryClient.invalidateQueries({ queryKey: usersKey });

  const updateRole = useMutation({
    mutationFn: (vars: { id: string; role: Role }) => {
      const body: UpdateUserRoleRequest = { role: vars.role };
      return api.patch<UserDto>(`/api/users/${vars.id}/role`, body);
    },
    onSuccess: invalidateUsers,
  });

  const updateAiEnabled = useMutation({
    mutationFn: (vars: { id: string; enabled: boolean }) =>
      api.put<UserDto>(`/api/users/${vars.id}/ai-enabled`, { enabled: vars.enabled }),
    onSuccess: invalidateUsers,
  });

  const updateLevelsEnabled = useMutation({
    mutationFn: (vars: { id: string; enabled: boolean }) =>
      api.put<UserDto>(`/api/users/${vars.id}/levels-enabled`, { enabled: vars.enabled }),
    onSuccess: invalidateUsers,
  });

  const updateCvEnabled = useMutation({
    mutationFn: (vars: { id: string; enabled: boolean }) =>
      api.put<UserDto>(`/api/users/${vars.id}/cv-enabled`, { enabled: vars.enabled }),
    onSuccess: invalidateUsers,
  });

  // --- Import da CSV -------------------------------------------------------
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportUsersResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importUsers = useMutation({
    mutationFn: (rows: ImportUserRow[]) => {
      const body: ImportUsersRequest = { users: rows };
      return api.post<ImportUsersResponse>('/api/users/import', body);
    },
    onSuccess: (result) => {
      setImportResult(result);
      invalidateUsers();
    },
  });

  const closeImport = () => {
    setIsImportOpen(false);
    setCsvError(null);
    setImportResult(null);
    importUsers.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCsvFile = async (file: File) => {
    setCsvError(null);
    setImportResult(null);
    importUsers.reset();
    try {
      const text = await file.text();
      const rows = parseUsersCsv(text);
      importUsers.mutate(rows);
    } catch (err) {
      setCsvError(
        err instanceof CsvParseError
          ? err.message
          : 'Impossibile leggere il file CSV.',
      );
    }
  };

  const [query, setQuery] = useState('');
  // Filtro per business unit: `'all'` (tutte) oppure l'`id` di una BU.
  const [departmentFilter, setDepartmentFilter] = useState<'all' | string>('all');

  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const departments = useMemo(
    () => departmentsQuery.data ?? [],
    [departmentsQuery.data],
  );

  // Filtro per business unit + ricerca client‑side per nome o email
  // (case‑insensitive, sottostringa). Filtra prima di passare le righe al
  // `DataTable`, così ordinamento e paginazione operano sull'insieme filtrato.
  const normalizedQuery = query.trim().toLowerCase();
  const visibleUsers = useMemo(() => {
    const byDepartment =
      departmentFilter === 'all'
        ? users
        : users.filter((u) => (u.departmentId ?? '') === departmentFilter);
    if (normalizedQuery === '') return byDepartment;
    return byDepartment.filter(
      (u) =>
        u.displayName.toLowerCase().includes(normalizedQuery) ||
        u.email.toLowerCase().includes(normalizedQuery),
    );
  }, [users, normalizedQuery, departmentFilter]);

  const mutating =
    updateRole.isPending ||
    updateAiEnabled.isPending ||
    updateLevelsEnabled.isPending ||
    updateCvEnabled.isPending;

  const columns = useMemo<Column<UserDto>[]>(
    () => [
      {
        key: 'displayName',
        header: 'Utente',
        className: 'user-admin__col-user',
        sortable: true,
        sortComparator: makeStringComparator((u) => u.displayName.toLowerCase()),
        render: (u) => (
          <div className="user-admin__identity">
            <span className="user-admin__name">{u.displayName}</span>
            <span className="user-admin__email">{u.email}</span>
          </div>
        ),
      },
      {
        key: 'role',
        header: 'Ruolo',
        className: 'user-admin__col-fit',
        sortable: true,
        sortComparator: makeStringComparator((u) => ROLE_LABEL[u.role]),
        render: (u) => (
          <label className="user-admin__control">
            <span className="sr-only">Ruolo di {u.displayName}</span>
            <span
              className={`user-admin__select user-admin__select--role user-admin__select--role-${u.role}`}
            >
              <select
                value={u.role}
                disabled={mutating}
                aria-label={`Ruolo di ${u.displayName}`}
                onChange={(e) =>
                  updateRole.mutate({ id: u.id, role: e.target.value as Role })
                }
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </span>
          </label>
        ),
      },
      {
        key: 'aiEnabled',
        header: 'AI',
        className: 'user-admin__col-fit',
        sortable: true,
        sortComparator: (a, b, direction) => {
          const sign = direction === 'desc' ? -1 : 1;
          const va = a.aiEnabled ? 1 : 0;
          const vb = b.aiEnabled ? 1 : 0;
          return (va - vb) * sign;
        },
        render: (u) => (
          <div className="user-admin__control user-admin__control--ai">
            <Toggle
              checked={u.aiEnabled}
              disabled={mutating}
              aria-label={`Valutazione AI di ${u.displayName}`}
              onChange={(enabled) =>
                updateAiEnabled.mutate({ id: u.id, enabled })
              }
            />
          </div>
        ),
      },
      {
        key: 'levelsEnabled',
        header: 'Insights',
        className: 'user-admin__col-fit',
        sortable: true,
        sortComparator: (a, b, direction) => {
          const sign = direction === 'desc' ? -1 : 1;
          const va = a.levelsEnabled ? 1 : 0;
          const vb = b.levelsEnabled ? 1 : 0;
          return (va - vb) * sign;
        },
        render: (u) => (
          <div className="user-admin__control user-admin__control--levels">
            <Toggle
              checked={u.levelsEnabled}
              disabled={mutating}
              aria-label={`Accesso alla sezione Livelli di ${u.displayName}`}
              onChange={(enabled) =>
                updateLevelsEnabled.mutate({ id: u.id, enabled })
              }
            />
          </div>
        ),
      },
      {
        key: 'cvEnabled',
        header: 'CV',
        className: 'user-admin__col-fit',
        sortable: true,
        sortComparator: (a, b, direction) => {
          const sign = direction === 'desc' ? -1 : 1;
          const va = a.cvEnabled ? 1 : 0;
          const vb = b.cvEnabled ? 1 : 0;
          return (va - vb) * sign;
        },
        render: (u) => (
          <div className="user-admin__control user-admin__control--levels">
            <Toggle
              checked={u.cvEnabled}
              disabled={mutating}
              aria-label={`Accesso alla sezione CV di ${u.displayName}`}
              onChange={(enabled) =>
                updateCvEnabled.mutate({ id: u.id, enabled })
              }
            />
          </div>
        ),
      },
    ],
    [
      mutating,
      updateRole,
      updateAiEnabled,
      updateLevelsEnabled,
      updateCvEnabled,
    ],
  );

  return (
    <section className="user-admin" aria-labelledby="user-admin-title">
      <PageHeader
        title="Amministrazione — Utenti"
        titleId="user-admin-title"
        subtitle="Gestisci ruoli e business unit degli utenti dell'organizzazione."
        actions={
          <div className="admin-toolbar">
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                setImportResult(null);
                setCsvError(null);
                importUsers.reset();
                setIsImportOpen(true);
              }}
            >
              Importa da CSV
            </Button>
          </div>
        }
      />

      {/* Ricerca a piena larghezza con filtro business unit integrato (select a
          1/4, input per il resto), stesso formato delle altre pagine admin. */}
      <Field
        id="user-search"
        label="Cerca utente"
        hint="Filtra per business unit, nome o email. La ricerca opera nella business unit selezionata."
      >
        <div className="competence-search">
          <span className="competence-search__department user-admin__select">
            <select
              id="user-search-department"
              aria-label="Filtra per business unit"
              value={departmentFilter}
              disabled={departmentsQuery.isLoading}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="all">Tutte le business unit</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </span>
          <TextInput
            id="user-search"
            className="competence-search__input"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome o email…"
            autoComplete="off"
            aria-label="Cerca per nome o email"
          />
        </div>
      </Field>

      {/* Import utenti da file CSV (de‑dup per email lato server). */}
      <Modal
        open={isImportOpen}
        onClose={closeImport}
        title="Importa utenti da CSV"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={closeImport}>
              {importResult ? 'Chiudi' : 'Annulla'}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={importUsers.isPending}
              disabled={importUsers.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              Scegli file CSV…
            </Button>
          </>
        }
      >
        <div className="admin-form">
          <p>
            Carica un file CSV con una colonna <code>email</code> (obbligatoria) e,
            facoltativamente, <code>displayName</code> e <code>role</code>. Gli utenti
            con un'email già presente vengono saltati: l'email è la chiave.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            aria-label="File CSV da importare"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleCsvFile(file);
            }}
          />

          {csvError && <Alert tone="error">{csvError}</Alert>}

          {importUsers.isError && (
            <Alert tone="error">
              Import non riuscito. Verifica il file e riprova.
            </Alert>
          )}

          {importResult && (
            <Alert tone={importResult.created > 0 ? 'success' : 'info'}>
              Importati {importResult.created} utenti,{' '}
              {importResult.skipped} saltati
              {importResult.skippedEmails.length > 0 && (
                <> (già presenti: {importResult.skippedEmails.join(', ')})</>
              )}
              .
            </Alert>
          )}
        </div>
      </Modal>

      {(updateRole.isError ||
        updateAiEnabled.isError ||
        updateLevelsEnabled.isError ||
        updateCvEnabled.isError) && (
        <Alert tone="error">
          Impossibile salvare la modifica. Riprova.
        </Alert>
      )}

      {departmentsQuery.isError && (
        <Alert tone="warning">
          Impossibile caricare le business unit: l'assegnazione della business unit è temporaneamente
          non disponibile.
        </Alert>
      )}

      {usersQuery.isLoading ? (
        <Loading>Caricamento utenti…</Loading>
      ) : (
        <DataTable
          rows={visibleUsers}
          columns={columns}
          rowKey={(u) => u.id}
          error={usersQuery.isError ? 'Impossibile caricare gli utenti.' : null}
          emptyMessage={
            normalizedQuery !== '' || departmentFilter !== 'all'
              ? 'Nessun utente corrisponde alla business unit o alla ricerca.'
              : 'Nessun utente da mostrare.'
          }
          defaultSort={{ key: 'displayName', direction: 'asc' }}
          pageSize={10}
        />
      )}
    </section>
  );
}
